import type { CompareBatch } from '@/types/compare'

type BatchListener = (batch: CompareBatch) => void

interface StreamState {
  batch: CompareBatch
  listeners: Set<BatchListener>
  active: boolean
  abort: (() => void) | null
}

const streams = new Map<string, StreamState>()

export function initCompareStream(batch: CompareBatch, abort?: () => void) {
  streams.set(batch.id, { batch, listeners: new Set(), active: true, abort: abort ?? null })
  notify(batch.id)
}

export function abortCompareStream(batchId: string) {
  const state = streams.get(batchId)
  if (!state) return
  state.abort?.()
  state.active = false
  notify(batchId)
}

export function subscribeCompareStream(batchId: string, listener: BatchListener): () => void {
  const state = streams.get(batchId)
  if (!state) return () => {}

  state.listeners.add(listener)
  listener(state.batch)

  return () => {
    state.listeners.delete(listener)
  }
}

export function updateCompareStream(
  batchId: string,
  updater: (batch: CompareBatch) => CompareBatch,
) {
  const state = streams.get(batchId)
  if (!state) return

  state.batch = updater(state.batch)
  notify(batchId)
}

export function finishCompareStream(batchId: string) {
  const state = streams.get(batchId)
  if (!state) return

  state.active = false
  notify(batchId)
}

export function isCompareStreamActive(batchId: string): boolean {
  return streams.get(batchId)?.active ?? false
}

export function getCompareStreamBatch(batchId: string): CompareBatch | null {
  return streams.get(batchId)?.batch ?? null
}

export function clearCompareStream(batchId: string) {
  streams.delete(batchId)
}

function notify(batchId: string) {
  const state = streams.get(batchId)
  if (!state) return

  for (const listener of state.listeners) {
    listener(state.batch)
  }
}

export function resetCompareStreamStore() {
  streams.clear()
}
